import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Menu, X, LogOut, User } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import NotificationBell from './NotificationBell';

function Navbar() {
    const { isAuthenticated, logout, user } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [scrolled, setScrolled] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 20);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const handleLogout = async () => {
        await logout();
        navigate('/auth/login');
    };

    const isAdmin = user?.is_staff || user?.user_type === 'admin' || user?.user_type === 'campus_admin';
    const coursesPath = isAdmin ? '/admin/courses' : '/courses';

    const navLinkStyle = (path) => ({
        fontWeight: 500,
        color: location.pathname === path ? 'var(--color-primary)' : 'var(--color-text-secondary)',
        transition: 'color var(--transition-fast)'
    });

    return (
        <nav className={`navbar flex items-center justify-between w-full ${scrolled ? 'glass' : ''}`} style={{
            padding: '1rem var(--spacing-xl)',
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 1000,
            transition: 'all 0.3s ease',
            backgroundColor: scrolled ? 'var(--glass-bg)' : 'transparent',
            borderBottom: scrolled ? 'var(--glass-border)' : '1px solid transparent'
        }}>
            <Link to="/" className="flex items-center gap-sm">
                <div style={{
                    background: 'var(--gradient-brand)',
                    color: 'white',
                    width: '36px',
                    height: '36px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '8px',
                    fontWeight: 800,
                    fontSize: '1.2rem',
                    boxShadow: 'var(--shadow-md)'
                }}>L</div>
                <span style={{ fontWeight: 800, fontSize: '1.5rem', letterSpacing: '-0.5px' }}>Learnly</span>
            </Link>

            <div className="flex gap-lg items-center hidden-mobile" style={{ display: 'flex' }}>
                <Link to="/" style={navLinkStyle('/')} className="hover:text-primary">Home</Link>
                <Link to={coursesPath} style={navLinkStyle(coursesPath)} className="hover:text-primary">Courses</Link>
                {isAuthenticated && (
                    <Link 
                        to={isAdmin ? '/admin/assessments' : '/assessments'} 
                        style={navLinkStyle(isAdmin ? '/admin/assessments' : '/assessments')} 
                        className="hover:text-primary"
                    >
                        Assessments
                    </Link>
                )}
                <Link to="/about" style={navLinkStyle('/about')} className="hover:text-primary">About</Link>
                <Link to="/contact" style={navLinkStyle('/contact')} className="hover:text-primary">Contact Us</Link>
                {isAuthenticated && (
                    <>
                        <Link to="/dashboard" style={{ fontWeight: 600, color: 'var(--color-primary)' }}>Dashboard</Link>
                        {(user?.is_staff || user?.user_type === 'admin' || user?.user_type === 'campus_admin') && (
                            <Link to="/admin/enrollments" style={{ fontWeight: 600, color: 'var(--color-primary)' }}>Admin</Link>
                        )}
                    </>
                )}
            </div>

            <div className="flex gap-md items-center">
                {isAuthenticated ? (
                    <div className="flex gap-md items-center">
                        <NotificationBell variant="navbar" inboxPath="/notifications" />

                        <Link to="/profile" className="flex items-center gap-xs btn-outline" style={{ padding: '0.5rem 1rem', borderRadius: 'var(--radius-pill)', fontWeight: 500, color: 'var(--color-text-primary)' }}>
                            <User size={16} /> {user?.first_name || 'Account'}
                        </Link>
                        <button onClick={handleLogout} className="flex items-center gap-xs btn-ghost" style={{ padding: '0.5rem' }} title="Logout">
                            <LogOut size={18} />
                        </button>
                    </div>
                ) : (
                    <div className="flex gap-sm items-center">
                        <Link to="/auth/login" className="btn btn-ghost" style={{ fontWeight: 600, color: 'var(--color-text-primary)' }}>Login</Link>
                        <Link to="/auth/register" className="btn btn-primary">Start Free Trial</Link>
                    </div>
                )}
            </div>
        </nav>
    );
}

export default Navbar;

